import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Container,
  FormControl,
  Grid,
  Icon,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Pagination,
  CircularProgress,
  Backdrop
} from "@mui/material";
import {
  DownloadRounded,
  FileDownloadOutlined,
  PictureAsPdfOutlined,
  CheckCircleOutline,
  HourglassEmpty,
  ReportProblemOutlined,
  Refresh
} from "@mui/icons-material";
import {
  exportClaimsReport,
  getAllClaimsData,
  getReconciliationQueue,
  getReconciliationSummary,
  resolveReconciliationItem
} from "../../../services/dashboard";
import ButtonComp from "../../../components/Button";
import CloseIcon from "@mui/icons-material/Close";
import { useAuthStore } from "../../../store/useAuthStore";
import { useSessionStore } from "../../../store/useSessionStore";

interface ClaimsReportFilters {
  dateType: string;
  startDate: string;
  endDate: string;
  claimStatus: string;
  claimType: string;
}

const initialClaimsFilters: ClaimsReportFilters = {
  dateType: "CAPTURED_DATE",
  startDate: "",
  endDate: "",
  claimStatus: "",
  claimType: "",
};

export default function Executive() {
  const [activeTab, setActiveTab] = useState(0); // 0 = Discrepancy Report, 1 = Reconciliation Queue
  const { userId } = useAuthStore();
  const { userIdData } = useSessionStore();
  const currentUserId = userId || userIdData;

  // --- TAB 0: CLAIMS DISCREPANCY REPORT STATES ---
  const [claims, setClaims] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [dateType, setDateType] = useState("CAPTURED_DATE");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [claimStatus, setClaimStatus] = useState("");
  const [claimType, setClaimType] = useState("");
  const [claimsLoading, setClaimsLoading] = useState(true);
  const [claimsError, setClaimsError] = useState<string | null>(null);
  const [appliedFilters, setAppliedFilters] = useState<ClaimsReportFilters>(initialClaimsFilters);
  const [exportingFormat, setExportingFormat] = useState<"CSV" | "EXCEL" | "PDF" | null>(null);
  const claimsRequestSequence = useRef(0);
  const reconciliationRequestSequence = useRef(0);

  const hasFilters = Boolean(claimStatus || claimType || startDate || endDate);

  const applyClaimsFilters = () => {
    setClaimsLoading(true);
    setAppliedFilters({ dateType, startDate, endDate, claimStatus, claimType });
  };

  // --- TAB 1: RECONCILIATION QUEUE STATES ---
  const [recoItems, setRecoItems] = useState<any[]>([]);
  const [recoStatus, setRecoStatus] = useState("OPEN");
  const [recoDefect, setRecoDefect] = useState("");
  const [recoPolicy, setRecoPolicy] = useState("");
  const [recoPage, setRecoPage] = useState(0);
  const [recoTotalPages, setRecoTotalPages] = useState(0);
  const [recoLoading, setRecoLoading] = useState(false);
  const [recoSummary, setRecoSummary] = useState<any>(null);

  // Resolve Dialog States
  const [resolveOpen, setResolveOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [resolving, setResolving] = useState(false);

  // --- API CALLS ---
  const getAllReports = async (filters: ClaimsReportFilters = appliedFilters) => {
    const requestSequence = ++claimsRequestSequence.current;
    setClaimsLoading(true);
    setClaimsError(null);
    try {
      const statusFilter = filters.claimStatus && filters.claimStatus !== "ALL" ? filters.claimStatus : undefined;
      const typeFilter = filters.claimType && filters.claimType !== "ALL" ? filters.claimType : undefined;
      const response = await getAllClaimsData(
        filters.dateType,
        filters.startDate || undefined,
        filters.endDate || undefined,
        statusFilter,
        typeFilter
      );
      if (requestSequence !== claimsRequestSequence.current) return;
      if (response && response.status === 200) {
        setClaims(response?.data?.records || []);
        setSummary(response?.data || null);
      } else {
        console.error("Error loading executive reports:", response);
        setClaimsError("Unable to fetch report records. Click Refresh to try again.");
      }
    } catch (err: any) {
      if (requestSequence !== claimsRequestSequence.current) return;
      console.error("Exception in getAllReports:", err);
      setClaimsError("Network error while loading report records.");
    } finally {
      if (requestSequence === claimsRequestSequence.current) setClaimsLoading(false);
    }
  };

  const processClaimRows = (rows: any[]) => {
    if (!rows || rows.length === 0) return [];
    
    const result: any[] = [];
    let groupCounter = 0;
    let i = 0;
    while (i < rows.length) {
      const currentClaimNumber = rows[i].claim_number;
      
      // Count how many consecutive rows have the same claim_number
      let count = 0;
      while (i + count < rows.length && rows[i + count].claim_number === currentClaimNumber) {
        count++;
      }
      
      for (let j = 0; j < count; j++) {
        result.push({
          ...rows[i + j],
          rowSpan: count,
          isFirstOfGroup: j === 0,
          isLastOfGroup: j === count - 1,
          groupColorIndex: groupCounter
        });
      }
      groupCounter++;
      i += count;
    }
    return result;
  };

  const fetchReconciliationQueue = async () => {
    const requestSequence = ++reconciliationRequestSequence.current;
    setRecoLoading(true);
    const params = {
      status: recoStatus || undefined,
      defectClass: recoDefect || undefined,
      policyNumber: recoPolicy || undefined,
      page: recoPage,
      size: 10,
      sortBy: "createdOn",
      direction: "desc"
    };
    const res = await getReconciliationQueue(params);
    if (requestSequence !== reconciliationRequestSequence.current) return;
    setRecoLoading(false);
    if (res?.status === 200) {
      setRecoItems(res.data?.content || []);
      setRecoTotalPages(res.data?.totalPages || 0);
    }
    const summaryRes = await getReconciliationSummary();
    if (requestSequence !== reconciliationRequestSequence.current) return;
    if (summaryRes?.status === 200) {
      setRecoSummary(summaryRes.data);
    }
  };

  const handleExport = async (format: "CSV" | "EXCEL" | "PDF") => {
    if (claimsLoading || exportingFormat) return;
    setExportingFormat(format);
    try {
      const statusFilter = appliedFilters.claimStatus && appliedFilters.claimStatus !== "ALL" ? appliedFilters.claimStatus : undefined;
      const typeFilter = appliedFilters.claimType && appliedFilters.claimType !== "ALL" ? appliedFilters.claimType : undefined;
      const response = await exportClaimsReport(format, {
        dateType: appliedFilters.dateType,
        startDate: appliedFilters.startDate || undefined,
        endDate: appliedFilters.endDate || undefined,
        claimStatus: statusFilter,
        claimType: typeFilter,
      });

      const exportedRecords = Number(response.headers?.["x-report-total-records"]);
      const displayedRecords = Number(summary?.total_records ?? claims.length);

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const count = Number.isFinite(exportedRecords) && exportedRecords > 0 ? exportedRecords : displayedRecords;
      const ext = format === "EXCEL" ? "xlsx" : format.toLowerCase();
      link.download = `claims_report_${appliedFilters.dateType.toLowerCase()}_${appliedFilters.startDate || "all"}_to_${appliedFilters.endDate || "all"}_${count}_records.${ext}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      if (Number.isFinite(exportedRecords) && exportedRecords !== displayedRecords) {
        await getAllReports(appliedFilters);
      }
    } catch (error) {
      console.error("Export failed", error);
    } finally {
      setExportingFormat(null);
    }
  };

  const handleOpenResolve = (item: any) => {
    setSelectedItem(item);
    setResolutionNotes("");
    setResolveOpen(true);
  };

  const handleSubmitResolve = async () => {
    if (!resolutionNotes.trim()) return;
    setResolving(true);
    const payload = {
      user_id: currentUserId,
      notes: resolutionNotes
    };
    const res = await resolveReconciliationItem(selectedItem.id, payload);
    setResolving(false);
    if (res?.status === 200) {
      setResolveOpen(false);
      fetchReconciliationQueue();
    } else {
      window.alert(res?.data?.message || "Failed to resolve ticket.");
    }
  };

  useEffect(() => {
    if (activeTab === 0) getAllReports(appliedFilters);
  }, [activeTab, appliedFilters]);

  useEffect(() => {
    if (activeTab === 1) fetchReconciliationQueue();
  }, [activeTab, recoStatus, recoDefect, recoPage]);

  // Handle policy search separately to avoid spamming the backend
  useEffect(() => {
    if (activeTab === 1) {
      const delayDebounce = setTimeout(() => {
        fetchReconciliationQueue();
      }, 500);
      return () => clearTimeout(delayDebounce);
    }
  }, [recoPolicy]);

  const formatDefectClass = (value: string) => {
    if (!value) return "-";
    return value.replaceAll("_", " ");
  };

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        bgcolor: "#F8F9FB",
        overflowX: "hidden",
      }}
    >
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4, overflowX: "hidden", position: "relative" }}>
        <Box
          sx={{ mb: 2 }}
          display="flex"
          justifyContent="space-between"
          flexDirection={{ md: "row", xs: "column" }}
        >
          <Box textAlign="left">
            <Typography
              fontSize={{ xs: 18, sm: 22, md: 25 }}
              sx={{ fontWeight: 600, color: "primary.main" }}
            >
              Claims Executive & Reconciliation Report
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Monitor claim discrepancies, override logs, and Easipol master data corrections.
            </Typography>
          </Box>
          {activeTab === 0 && (
            <Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <ButtonComp variant="contained" height={40} width={120} disabled={claimsLoading || Boolean(exportingFormat)} onClick={() => handleExport("CSV")}>
                  <Icon><FileDownloadOutlined /></Icon>
                  {exportingFormat === "CSV" ? "Exporting…" : "CSV"}
                </ButtonComp>
                <ButtonComp variant="outlined" height={40} width={120} disabled={claimsLoading || Boolean(exportingFormat)} onClick={() => handleExport("EXCEL")}>
                  <Icon><FileDownloadOutlined /></Icon>
                  {exportingFormat === "EXCEL" ? "Exporting…" : "EXCEL"}
                </ButtonComp>
                <ButtonComp variant="contained" height={40} width={120} color="secondary" disabled={claimsLoading || Boolean(exportingFormat)} onClick={() => handleExport("PDF")}>
                  <Icon><PictureAsPdfOutlined /></Icon>
                  {exportingFormat === "PDF" ? "Exporting…" : "PDF"}
                </ButtonComp>
              </Stack>
            </Box>
          )}
        </Box>

        <Tabs
          value={activeTab}
          onChange={(e, val) => {
            setActiveTab(val);
            setRecoPage(0);
          }}
          sx={{
            mb: 3,
            borderBottom: "1px solid #DFDDDD",
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: "14px",
            }
          }}
        >
          <Tab label="Claims Discrepancy Report" />
          <Tab label="Easipol Reconciliation Queue" />
        </Tabs>

        <Backdrop
          open={activeTab === 0 && claimsLoading}
          sx={{
            position: "absolute",
            zIndex: (theme) => theme.zIndex.drawer + 1,
            bgcolor: "rgba(248, 249, 251, 0.82)",
            backdropFilter: "blur(2px)",
            alignItems: "flex-start",
            pt: { xs: 24, md: 20 },
          }}
        >
          <Paper
            elevation={3}
            role="status"
            aria-label="Loading executive claims report"
            sx={{ px: 4, py: 3, borderRadius: 3, display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}
          >
            <CircularProgress size={42} thickness={4} />
            <Typography fontWeight={600} color="text.primary">Loading report…</Typography>
            <Typography variant="caption" color="text.secondary">Please wait while the claims data is prepared.</Typography>
          </Paper>
        </Backdrop>

        {activeTab === 0 ? (
          // ==================== TAB 0: DISCREPANCY REPORT ====================
          <>
            <Grid container spacing={2} mb={2}>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Paper
                  sx={{
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    borderLeft: "4px solid #3B82F6",
                    background: "#ffffff",
                    justifyContent: "center",
                    alignContent: "center",
                  }}
                >
                  <Box display="flex" flexDirection="row">
                    <Box>
                      <Typography variant="body2">Unique Claims</Typography>
                      <Typography variant="h5" fontWeight={600}>
                        {summary?.total_claims ?? summary?.reconciliation_summary?.total_claims ?? 0}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Paper
                  sx={{
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    borderLeft: "4px solid #4CAF50",
                    background: "#ffffff",
                    justifyContent: "center",
                    alignContent: "center",
                  }}
                >
                  <Box display="flex" flexDirection="row">
                    <Box>
                      <Typography variant="body2">Total Claim Amount</Typography>
                      <Typography variant="h5" fontWeight={600}>
                        R{summary?.total_claim_amount?.toLocaleString() || 0}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Paper
                  sx={{
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    borderLeft: "4px solid #EF4444",
                    background: "#ffffff",
                    justifyContent: "center",
                    alignContent: "center",
                  }}
                >
                  <Box display="flex" flexDirection="row">
                    <Box>
                      <Typography variant="body2">Reconciliation Issues</Typography>
                      <Typography variant="h5" fontWeight={600}>
                        {summary?.reconciliation_summary?.missing_in_easipol || 0}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            <Typography fontSize={16} fontWeight={600} textAlign="left" mt={4} mb={1.5} color="#1e293b">
              Claim Detail Dataset {summary ? `(${summary.total_records ?? claims.length} rows)` : ""}
            </Typography>
            <Box sx={{
              p: 2,
              mb: 2,
              bgcolor: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
              alignItems: "center",
              justifyContent: "space-between"
            }}>
              <Typography variant="subtitle2" color="#475569" fontWeight={600}>
                Dataset Filters
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ gap: 1, justifyContent: "flex-end" }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Date Type</InputLabel>
                  <Select
                    value={dateType}
                    label="Date Type"
                    onChange={(e) => setDateType(e.target.value)}
                    sx={{ fontSize: "12px", bgcolor: "#ffffff", borderRadius: "6px" }}
                  >
                    <MenuItem value="CAPTURED_DATE" sx={{ fontSize: 12 }}>Captured</MenuItem>
                    <MenuItem value="APPROVAL_DATE" sx={{ fontSize: 12 }}>Approved</MenuItem>
                    <MenuItem value="PAYMENT_DATE" sx={{ fontSize: 12 }}>Payment</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  size="small"
                  type="date"
                  label="From"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    width: 130,
                    "& .MuiInputBase-root": { bgcolor: "#ffffff", borderRadius: "6px" },
                    "& .MuiInputBase-input": { fontSize: "12px", padding: "8.5px 8px" },
                    "& .MuiInputLabel-root": { fontSize: "16px" },
                  }}
                />

                <TextField
                  size="small"
                  type="date"
                  label="To"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    width: 130,
                    "& .MuiInputBase-root": { bgcolor: "#ffffff", borderRadius: "6px" },
                    "& .MuiInputBase-input": { fontSize: "12px", padding: "8.5px 8px" },
                    "& .MuiInputLabel-root": { fontSize: "16px" },
                  }}
                />

                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel sx={{ fontSize: 12 }}>Status</InputLabel>
                  <Select
                    value={claimStatus}
                    label="Status"
                    onChange={(e) => setClaimStatus(e.target.value)}
                    sx={{ fontSize: "12px", bgcolor: "#ffffff", borderRadius: "6px" }}
                  >
                    <MenuItem value="" sx={{ fontSize: 12 }}>All Statuses</MenuItem>
                    <MenuItem value="DRAFT" sx={{ fontSize: 12 }}>Draft</MenuItem>
                    <MenuItem value="NEW" sx={{ fontSize: 12 }}>New</MenuItem>
                    <MenuItem value="VETTED" sx={{ fontSize: 12 }}>Vetted</MenuItem>
                    <MenuItem value="APPROVED" sx={{ fontSize: 12 }}>Approved</MenuItem>
                    <MenuItem value="PAID" sx={{ fontSize: 12 }}>Paid</MenuItem>
                    <MenuItem value="CASH_PAID" sx={{ fontSize: 12 }}>Cash Paid</MenuItem>
                    <MenuItem value="SERVICE_PAID" sx={{ fontSize: 12 }}>Service Paid</MenuItem>
                    <MenuItem value="DECLINED" sx={{ fontSize: 12 }}>Declined</MenuItem>
                    <MenuItem value="CLOSED" sx={{ fontSize: 12 }}>Closed</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel sx={{ fontSize: 12 }}>Claim Type</InputLabel>
                  <Select
                    value={claimType}
                    label="Claim Type"
                    onChange={(e) => setClaimType(e.target.value)}
                    sx={{ fontSize: "12px", bgcolor: "#ffffff", borderRadius: "6px" }}
                  >
                    <MenuItem value="" sx={{ fontSize: 12 }}>All Types</MenuItem>
                    <MenuItem value="DEATH" sx={{ fontSize: 12 }}>Death</MenuItem>
                    <MenuItem value="TEN_PERCENT" sx={{ fontSize: 12 }}>Ten Percent</MenuItem>
                  </Select>
                </FormControl>

                <Button
                  size="small"
                  variant="contained"
                  onClick={applyClaimsFilters}
                  disabled={claimsLoading}
                  sx={{ textTransform: "none", minHeight: 34 }}
                >
                  {claimsLoading ? <><CircularProgress size={15} color="inherit" sx={{ mr: 1 }} />Loading…</> : "Apply filters"}
                </Button>

                <IconButton
                  size="small"
                  onClick={() => getAllReports(appliedFilters)}
                  disabled={claimsLoading}
                  title="Refresh Applied Report Data"
                  sx={{ border: "1px solid #cbd5e1", bgcolor: "#ffffff", p: 0.7 }}
                >
                  <Refresh fontSize="small" />
                </IconButton>

                {hasFilters && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      setClaimsLoading(true);
                      setClaimStatus("");
                      setClaimType("");
                      setStartDate("");
                      setEndDate("");
                      setAppliedFilters({ dateType, startDate: "", endDate: "", claimStatus: "", claimType: "" });
                    }}
                    title="Clear Filters"
                    sx={{ border: "1px solid #cbd5e1", bgcolor: "#ffffff", p: 0.7 }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Stack>
            </Box>

            <TableContainer sx={{
              maxHeight: 460,
              overflowX: "auto",
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05)",
              bgcolor: "#ffffff"
            }} aria-busy={claimsLoading}>
              <Table stickyHeader size="small" aria-label="sticky table" sx={{
                minWidth: "max-content",
                "& .MuiTableCell-root": { padding: "10px 16px", fontSize: "13px", whiteSpace: "nowrap" }
              }}>
                <TableHead sx={{
                  "& .MuiTableCell-root": {
                    backgroundColor: "#f8fafc",
                    color: "#475569",
                    fontWeight: 700,
                    fontSize: "13px",
                    borderBottom: "2px solid #e2e8f0",
                    p: 2
                  }
                }}>
                  <TableRow>
                    <TableCell>Claim Number</TableCell>
                    <TableCell>Type of Claim</TableCell>
                    <TableCell>Claim Status</TableCell>
                    <TableCell>Date Captured</TableCell>
                    <TableCell>Approved Date</TableCell>
                    <TableCell>Payment Date</TableCell>
                    <TableCell>Policy Number</TableCell>
                    <TableCell>Main Member Name</TableCell>
                    <TableCell>Deceased Name</TableCell>
                    <TableCell>Main Plan Name</TableCell>
                    <TableCell>Main Plan Payout Type</TableCell>
                    <TableCell align="right">Main Plan Cash Payout Amount</TableCell>
                    <TableCell align="right">Main Plan Services Payout Amount</TableCell>
                    <TableCell align="right">Main Plan Total Benefit Amount</TableCell>
                    <TableCell>Top-Up Name</TableCell>
                    <TableCell align="right">Top-Up Cash Payout Amount</TableCell>
                    <TableCell align="right">Top-Up Services Payout Amount</TableCell>
                    <TableCell align="right">Top-Up Amount</TableCell>
                    <TableCell align="right">Total Claim Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody sx={{
                  "& .MuiTableRow-root:hover": {
                    backgroundColor: "#f8fafc",
                    transition: "background-color 0.2s ease"
                  }
                }}>
                  {claimsLoading ? (
                    <TableRow>
                      <TableCell colSpan={19} align="center" sx={{ py: 6 }}>
                        <Stack spacing={2} alignItems="center" justifyContent="center">
                          <CircularProgress size={32} />
                          <Typography variant="body2" color="text.secondary">Loading claims executive dataset…</Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ) : claimsError ? (
                    <TableRow>
                      <TableCell colSpan={19} align="center" sx={{ py: 4, bgcolor: "#fef2f2" }}>
                        <Stack spacing={1} alignItems="center" justifyContent="center">
                          <Typography variant="body2" color="error.main" fontWeight={600}>{claimsError}</Typography>
                          <Button size="small" variant="outlined" color="error" onClick={() => getAllReports(appliedFilters)}>
                            Retry Loading Reports
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ) : (() => {
                    const processedRows = processClaimRows(claims);
                    return processedRows.length ? (
                      processedRows.map((item: any, index: number) => {
                        const isEvenGroup = item.groupColorIndex % 2 === 0;
                        const rowBgColor = isEvenGroup ? "#ffffff" : "#f8fafc";
                        const rowBottomBorder = item.isLastOfGroup ? "2px solid #e2e8f0" : "1px dashed #e2e8f0";
                        
                        return (
                          <TableRow key={index} sx={{ backgroundColor: rowBgColor, "&:hover": { backgroundColor: "#f1f5f9 !important" } }}>
                            {item.isFirstOfGroup && (
                              <>
                                <TableCell rowSpan={item.rowSpan} sx={{ fontWeight: 600, color: "#1e293b", borderBottom: "2px solid #cbd5e1", verticalAlign: "middle" }}>{item.claim_number}</TableCell>
                                <TableCell rowSpan={item.rowSpan} sx={{ color: "#334155", borderBottom: "2px solid #cbd5e1", verticalAlign: "middle" }}>
                                  <Chip
                                    label={item.type_of_claim || "N/A"}
                                    size="small"
                                    sx={{
                                      bgcolor: item.type_of_claim === "DEATH" ? "#eff6ff" : "#f5f3ff",
                                      color: item.type_of_claim === "DEATH" ? "#1d4ed8" : "#6d28d9",
                                      fontWeight: 600,
                                      fontSize: "10px",
                                      height: "18px"
                                    }}
                                  />
                                </TableCell>
                                <TableCell rowSpan={item.rowSpan} sx={{ borderBottom: "2px solid #cbd5e1", verticalAlign: "middle" }}>
                                  {(() => {
                                    const colors: any = {
                                      DRAFT: { bg: "#f1f5f9", text: "#475569" },
                                      NEW: { bg: "#eff6ff", text: "#1d4ed8" },
                                      VETTED: { bg: "#fef3c7", text: "#b45309" },
                                      APPROVED: { bg: "#f0fdf4", text: "#15803d" },
                                      PAID: { bg: "#f0fdf4", text: "#16a34a" },
                                      CASH_PAID: { bg: "#f0fdf4", text: "#16a34a" },
                                      SERVICE_PAID: { bg: "#f0fdf4", text: "#16a34a" },
                                      DECLINED: { bg: "#fef2f2", text: "#b91c1c" },
                                      CLOSED: { bg: "#f8fafc", text: "#64748b" }
                                    };
                                    const style = colors[item.claim_status] || { bg: "#f1f5f9", text: "#475569" };
                                    return (
                                      <Chip
                                        label={item.claim_status || "N/A"}
                                        size="small"
                                        sx={{
                                          bgcolor: style.bg,
                                          color: style.text,
                                          fontWeight: 600,
                                          fontSize: "10px",
                                          height: "18px"
                                        }}
                                      />
                                    );
                                  })()}
                                </TableCell>
                                <TableCell rowSpan={item.rowSpan} sx={{ color: "#475569", borderBottom: "2px solid #cbd5e1", verticalAlign: "middle" }}>{item.date_captured}</TableCell>
                                <TableCell rowSpan={item.rowSpan} sx={{ color: "#475569", borderBottom: "2px solid #cbd5e1", verticalAlign: "middle" }}>{item.approved_date || "-"}</TableCell>
                                <TableCell rowSpan={item.rowSpan} sx={{ color: "#475569", borderBottom: "2px solid #cbd5e1", verticalAlign: "middle" }}>{item.payment_date || "-"}</TableCell>
                                <TableCell rowSpan={item.rowSpan} sx={{ color: "#334155", borderBottom: "2px solid #cbd5e1", verticalAlign: "middle" }}>{item.policy_number}</TableCell>
                                <TableCell rowSpan={item.rowSpan} sx={{ color: "#475569", borderBottom: "2px solid #cbd5e1", verticalAlign: "middle" }}>{item.main_member_name}</TableCell>
                                <TableCell rowSpan={item.rowSpan} sx={{ color: "#475569", borderBottom: "2px solid #cbd5e1", verticalAlign: "middle" }}>{item.deceased_name}</TableCell>
                                <TableCell rowSpan={item.rowSpan} sx={{ color: "#1e293b", fontWeight: 500, borderBottom: "2px solid #cbd5e1", verticalAlign: "middle" }}>{item.plan_type || "-"}</TableCell>
                                <TableCell rowSpan={item.rowSpan} sx={{ borderBottom: "2px solid #cbd5e1", verticalAlign: "middle" }}>
                                  {(() => {
                                    const type = item.main_plan_payout_type;
                                    if (!type) return "-";
                                    const colors: any = {
                                      "Cash and Services": { bg: "#ecfdf5", text: "#047857" },
                                      "Cash Payout": { bg: "#f0fdf4", text: "#15803d" },
                                      "Services Payout": { bg: "#f5f3ff", text: "#6d28d9" },
                                      "No Payout": { bg: "#f1f5f9", text: "#475569" }
                                    };
                                    const style = colors[type] || { bg: "#f1f5f9", text: "#475569" };
                                    return (
                                      <Chip
                                        label={type}
                                        size="small"
                                        sx={{
                                          bgcolor: style.bg,
                                          color: style.text,
                                          fontWeight: 600,
                                          fontSize: "10px",
                                          height: "18px"
                                        }}
                                      />
                                    );
                                  })()}
                                </TableCell>
                                <TableCell rowSpan={item.rowSpan} align="right" sx={{ borderBottom: "2px solid #cbd5e1", verticalAlign: "middle" }}>
                                  {(() => {
                                    const val = Number(item.main_plan_cash_payout_amount) || 0;
                                    if (val === 0) return <span style={{ color: "#94a3b8" }}>R0</span>;
                                    return <strong style={{ color: "#1e293b" }}>R{val.toLocaleString()}</strong>;
                                  })()}
                                </TableCell>
                                <TableCell rowSpan={item.rowSpan} align="right" sx={{ borderBottom: "2px solid #cbd5e1", verticalAlign: "middle" }}>
                                  {(() => {
                                    const val = Number(item.main_plan_services_payout_amount) || 0;
                                    if (val === 0) return <span style={{ color: "#94a3b8" }}>R0</span>;
                                    return <strong style={{ color: "#1e293b" }}>R{val.toLocaleString()}</strong>;
                                  })()}
                                </TableCell>
                                <TableCell rowSpan={item.rowSpan} align="right" sx={{ borderBottom: "2px solid #cbd5e1", verticalAlign: "middle" }}>
                                  {(() => {
                                    const val = Number(item.benefit_amount) || 0;
                                    if (val === 0) return <span style={{ color: "#94a3b8" }}>R0</span>;
                                    return <strong style={{ color: "#0f172a", fontSize: "14px" }}>R{val.toLocaleString()}</strong>;
                                  })()}
                                </TableCell>
                              </>
                            )}
                            
                            <TableCell sx={{ color: "#475569", borderBottom: rowBottomBorder }}>{item.top_up_name || "-"}</TableCell>
                            <TableCell align="right" sx={{ borderBottom: rowBottomBorder }}>
                              {(() => {
                                const val = Number(item.cash_payout_amount) || 0;
                                if (val === 0) return <span style={{ color: "#94a3b8" }}>R0</span>;
                                return <strong style={{ color: "#334155" }}>R{val.toLocaleString()}</strong>;
                              })()}
                            </TableCell>
                            <TableCell align="right" sx={{ borderBottom: rowBottomBorder }}>
                              {(() => {
                                const val = Number(item.services_payout_amount) || 0;
                                if (val === 0) return <span style={{ color: "#94a3b8" }}>R0</span>;
                                return <strong style={{ color: "#334155" }}>R{val.toLocaleString()}</strong>;
                              })()}
                            </TableCell>
                            <TableCell align="right" sx={{ borderBottom: rowBottomBorder }}>
                              {(() => {
                                const val = Number(item.top_up_amount) || 0;
                                if (val === 0) return <span style={{ color: "#94a3b8" }}>R0</span>;
                                return <strong style={{ color: "#334155" }}>R{val.toLocaleString()}</strong>;
                              })()}
                            </TableCell>
                            
                            {item.isFirstOfGroup && (
                              <TableCell rowSpan={item.rowSpan} align="right" sx={{ borderBottom: "2px solid #cbd5e1", verticalAlign: "middle" }}>
                                {(() => {
                                  const val = Number(item.total_claim_amount) || 0;
                                  if (val === 0) return <span style={{ color: "#94a3b8" }}>R0</span>;
                                  return <strong style={{ color: "#1e293b", fontWeight: 700, fontSize: "14px" }}>R{val.toLocaleString()}</strong>;
                                })()}
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={19} align="left">No claim details found for selected filters.</TableCell>
                      </TableRow>
                    );
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : (
          // ==================== TAB 1: RECONCILIATION QUEUE ====================
          <>
            <Grid container spacing={2} mb={2}>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Paper sx={{ p: 2, display: "flex", alignItems: "center", borderLeft: "4px solid #F59E0B", background: "#ffffff" }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Open Reconciliation Tickets</Typography>
                    <Typography variant="h5" fontWeight={600}>
                      {recoSummary?.open_count ?? 0}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Paper sx={{ p: 2, display: "flex", alignItems: "center", borderLeft: "4px solid #DC2626", background: "#ffffff" }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">SLA Breaches (&gt; 14 Days)</Typography>
                    <Typography variant="h5" fontWeight={600}>
                      {recoSummary?.sla_breached_count ?? 0}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Paper sx={{ p: 2, display: "flex", alignItems: "center", borderLeft: "4px solid #10B981", background: "#ffffff" }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Resolved Tickets</Typography>
                    <Typography variant="h5" fontWeight={600}>
                      {recoSummary?.resolved_count ?? 0}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            <Typography fontSize={16} fontWeight={600} textAlign="left" mt={4} mb={1}>
              Discrepancy Resolution Queue
            </Typography>
            <Paper elevation={2} sx={{ p: 2, bgcolor: "#ffffff", overflowX: "auto" }}>
              <Stack direction={{ xs: "column", md: "row" }} display="flex" justifyContent="flex-end" spacing={1} mb={2}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} flexWrap="wrap" useFlexGap>
                  <TextField
                    size="small"
                    placeholder="Search Policy No..."
                    value={recoPolicy}
                    onChange={(e) => setRecoPolicy(e.target.value)}
                    sx={{ width: 160 }}
                  />

                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={recoStatus}
                      label="Status"
                      onChange={(e) => {
                        setRecoStatus(e.target.value);
                        setRecoPage(0);
                      }}
                    >
                      <MenuItem value="">All Tickets</MenuItem>
                      <MenuItem value="OPEN">Open</MenuItem>
                      <MenuItem value="RESOLVED">Resolved</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Defect Class</InputLabel>
                    <Select
                      value={recoDefect}
                      label="Defect Class"
                      onChange={(e) => {
                        setRecoDefect(e.target.value);
                        setRecoPage(0);
                      }}
                    >
                      <MenuItem value="">All Defects</MenuItem>
                      <MenuItem value="RC_01">RC-01: Inception Date Reset</MenuItem>
                      <MenuItem value="RC_02">RC-02: Capture Date Error</MenuItem>
                      <MenuItem value="RC_03">RC-03: Phantom Benefit</MenuItem>
                      <MenuItem value="RC_04">RC-04: Duplicate Benefit</MenuItem>
                      <MenuItem value="RC_05">RC-05: Product Mismatch</MenuItem>
                      <MenuItem value="RC_06">RC-06: Disappeared Benefit</MenuItem>
                      <MenuItem value="RC_07">RC-07: Cover Discrepancy</MenuItem>
                      <MenuItem value="RC_08">RC-08: Other Issues</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
              </Stack>

              {recoLoading ? (
                <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
              ) : (
                <>
                  <TableContainer sx={{ overflowX: "auto" }}>
                    <Table stickyHeader size="small" sx={{
                      minWidth: "max-content",
                      "& .MuiTableCell-root": { padding: "10px 14px", fontSize: "13px", whiteSpace: "nowrap" }
                    }}>
                      <TableHead sx={{
                        "& .MuiTableCell-root": { backgroundColor: "background.paper", fontWeight: 600, fontSize: "14px", borderBottom: "1px solid #E5E7EB", p: 2 }
                      }}>
                        <TableRow>
                          <TableCell>Policy Number</TableCell>
                          <TableCell>Member Name</TableCell>
                          <TableCell>Claim Number</TableCell>
                          <TableCell>Defect Class</TableCell>
                          <TableCell>Date Logged</TableCell>
                          <TableCell align="center">Ageing</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>SLA Alert</TableCell>
                          <TableCell>Resolved By</TableCell>
                          <TableCell>Resolution Notes</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {recoItems.length ? (
                          recoItems.map((item: any, index: number) => (
                            <TableRow key={item.id} hover>
                              <TableCell>{item.policyNumber}</TableCell>
                              <TableCell>{item.memberName}</TableCell>
                              <TableCell>{item.claimNumber}</TableCell>
                              <TableCell>{formatDefectClass(item.defectClass)}</TableCell>
                              <TableCell>{new Date(item.createdOn).toLocaleDateString()}</TableCell>
                              <TableCell align="center">{item.ageingInDays} Days</TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={item.status}
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: "11px",
                                    color: item.status === "OPEN" ? "#B45309" : "#15803D",
                                    backgroundColor: item.status === "OPEN" ? "#FEF3C7" : "#DCFCE7"
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                {item.escalated ? (
                                  <Chip
                                    size="small"
                                    icon={<ReportProblemOutlined sx={{ fontSize: "12px !important", color: "#DC2626 !important" }} />}
                                    label="SLA Breached"
                                    sx={{ fontWeight: 700, fontSize: "10px", color: "#DC2626", backgroundColor: "#FEE2E2", border: "1px solid #FCA5A5" }}
                                  />
                                ) : (
                                  <Typography variant="caption" color="text.secondary">-</Typography>
                                )}
                              </TableCell>
                              <TableCell>{item.resolvedByUsername || "-"}</TableCell>
                              <TableCell sx={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}>
                                {item.resolutionNotes || "-"}
                              </TableCell>
                              <TableCell align="center">
                                {item.status === "OPEN" ? (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={<CheckCircleOutline />}
                                    onClick={() => handleOpenResolve(item)}
                                    sx={{
                                      textTransform: "none",
                                      borderRadius: "999px",
                                      fontSize: "11px",
                                      boxShadow: "none",
                                      px: 1.5,
                                      py: 0.5
                                    }}
                                  >
                                    Resolve
                                  </Button>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">Resolved</Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={11} align="center">No reconciliation items found.</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {recoTotalPages > 1 && (
                    <Box display="flex" justifyContent="flex-end" mt={2}>
                      <Pagination
                        count={recoTotalPages}
                        page={recoPage + 1}
                        onChange={(e, val) => setRecoPage(val - 1)}
                        color="primary"
                        size="small"
                      />
                    </Box>
                  )}
                </>
              )}
            </Paper>
          </>
        )}
      </Container>

      {/* RESOLVE DIALOG MODAL */}
      <Dialog
        open={resolveOpen}
        onClose={() => setResolveOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: "0 25px 50px -12px rgba(0,0,0,0.15)",
            border: "1px solid rgba(229, 231, 235, 0.6)"
          }
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 3, pt: 3, pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "rgba(37, 99, 235, 0.1)", color: "rgb(37, 99, 235)", borderRadius: "50%", p: 1 }}>
            <CheckCircleOutline fontSize="medium" />
          </Box>
          <DialogTitle sx={{ p: 0, fontWeight: 700, fontSize: "1.25rem", color: "text.primary" }}>
            Resolve Discrepancy Ticket
          </DialogTitle>
        </Box>

        <DialogContent sx={{ px: 3, pb: 2 }}>
          <Typography variant="body2" color="text.secondary" mb={2.5}>
            Confirm that this ticket's discrepancy has been corrected in Easipol. Review the details below and provide confirmation comments.
          </Typography>

          <Box
            sx={{
              bgcolor: "#F8F9FA",
              border: "1px solid #E9ECEF",
              borderRadius: "12px",
              p: 2.5,
              mb: 3
            }}
          >
            <Grid container spacing={2}>
              <Grid size={{ xs: 6, sm: 4 }}>
                <Typography fontSize={10} color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: 600 }}>
                  Policy Number
                </Typography>
                <Typography fontSize={13} fontWeight={700} color="text.primary" mt={0.5}>
                  {selectedItem?.policyNumber || "-"}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6, sm: 4 }}>
                <Typography fontSize={10} color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: 600 }}>
                  Member Name
                </Typography>
                <Typography fontSize={13} fontWeight={700} color="text.primary" mt={0.5}>
                  {selectedItem?.memberName || "-"}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography fontSize={10} color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: 600 }}>
                  Ageing
                </Typography>
                <Typography fontSize={13} fontWeight={700} color="text.primary" mt={0.5}>
                  {selectedItem?.ageingInDays || 0} Days
                </Typography>
              </Grid>
              <Grid size={12}>
                <Typography fontSize={10} color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: 600 }}>
                  Defect Class
                </Typography>
                <Typography fontSize={13} fontWeight={700} color="text.primary" mt={0.5}>
                  {selectedItem ? formatDefectClass(selectedItem.defectClass) : "-"}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          <TextField
            fullWidth
            multiline
            rows={4}
            label="Resolution Comments"
            variant="outlined"
            placeholder="Describe how the discrepancy was fixed on Easipol (e.g. corrected inception date/benefit cover mismatch resolved)..."
            value={resolutionNotes}
            onChange={(e) => setResolutionNotes(e.target.value)}
            InputLabelProps={{
              sx: { fontSize: "14px" }
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                fontSize: "14px",
                "& fieldset": {
                  borderColor: "#E2E8F0"
                },
                "&:hover fieldset": {
                  borderColor: "primary.main"
                }
              }
            }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            onClick={() => setResolveOpen(false)}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: "text.secondary",
              borderRadius: "8px",
              px: 2.5,
              "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04)" }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!resolutionNotes.trim() || resolving}
            onClick={handleSubmitResolve}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "8px",
              boxShadow: "none",
              px: 3,
              "&:hover": {
                boxShadow: "none",
                bgcolor: "primary.dark"
              }
            }}
          >
            {resolving ? "Saving..." : "Confirm Resolution"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
